// Company-configured default values for a connected store's product options.
//
// A row is catalog-wide: it names a modifier group and carries a value template, and the rows for one
// catalog and group are tried in index order until one renders. Rendering happens here rather than on the
// server because only the line item form knows which parent object and equipment a line carries.

const TEMPLATE_VARIABLE_PATTERN = /{{\s*([\w.]+)\s*}}/g

// Group types whose value is one of the store's options; every other type takes typed text.
const CHOICE_MODIFIER_TYPES = ["dropdown", "radio_buttons", "rectangles", "swatch", "product_list", "product_list_with_images"]

// Checkbox templates are written in plain words rather than the store's option ids.
const CHECKBOX_CHECKED_WORDS = ["yes", "y", "true", "1"]
const CHECKBOX_UNCHECKED_WORDS = ["no", "n", "false", "0"]

const ISO_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/

const resolveTemplateVariable = (context, path) => {
    // A variable resolves only to a printable scalar: an object would render as "[object Object]", and a
    // blank counts as missing so a fallback row can supply a better value than "Acme - ".
    const resolved = path.split(".").reduce(
        (current, segment) => (current === undefined || current === null ? undefined : current[segment]),
        context,
    )

    if (resolved === undefined || resolved === null || typeof resolved === "object" || String(resolved).trim() === "") {
        return undefined
    } else {
        return String(resolved)
    }
}

export const renderModifierTemplate = (template, context) => {
    let missingVariable = null

    const value = template.replace(TEMPLATE_VARIABLE_PATTERN, (match, path) => {
        const resolved = resolveTemplateVariable(context, path)

        if (resolved === undefined) {
            missingVariable = missingVariable ?? path
            return ""
        } else {
            return resolved
        }
    })

    return { value: value.trim(), missingVariable }
}

// Returns the selection value the picker stores for the group, or null when the rendered text cannot be
// applied to the group's input type. An empty string is a real selection: the unchecked checkbox state.
const applyModifierDefaultValue = (group, value) => {
    const normalizedValue = value.toLowerCase()
    const options = group.modifiers || []

    if (group.modifier_type === "checkbox") {
        const checkedOption = options.find(modifier => modifier.is_checked_state)

        if (CHECKBOX_CHECKED_WORDS.includes(normalizedValue) && checkedOption) {
            return String(checkedOption.external_id)
        } else if (CHECKBOX_UNCHECKED_WORDS.includes(normalizedValue)) {
            return ""
        } else {
            return null
        }
    } else if (CHOICE_MODIFIER_TYPES.includes(group.modifier_type)) {
        // A choice group matches the option's name first, then its store id; an option the store refuses to
        // sell is never picked.
        const purchasableOptions = options.filter(modifier => modifier.is_purchasable)
        const option = purchasableOptions.find(modifier => modifier.name.trim().toLowerCase() === normalizedValue)
            || purchasableOptions.find(modifier => String(modifier.external_id) === value)
        return option ? String(option.external_id) : null
    } else if (group.modifier_type === "numbers_only_text") {
        return Number.isNaN(Number(value)) ? null : value
    } else if (group.modifier_type === "date") {
        return ISO_DATE_PATTERN.test(value) && !Number.isNaN(Date.parse(value)) ? value : null
    } else {
        return value
    }
}

export const chooseModifierDefaultSelection = (group, modifierDefaults, context) => {
    // The rows arrive ordered by their stored name, so rows that spell the group name with different casing
    // are re-ordered here by index alone.
    const groupName = group.name.trim().toLowerCase()
    const rows = modifierDefaults
        .filter(row => row.modifier_name.trim().toLowerCase() === groupName)
        .sort((first, second) => first.index - second.index)

    for (const row of rows) {
        const rendered = renderModifierTemplate(row.value_template, context)
        const selection = rendered.missingVariable === null ? applyModifierDefaultValue(group, rendered.value) : null

        if (rendered.missingVariable !== null) {
            console.debug(`Skipped "${row.modifier_name}" default #${row.index}: ${rendered.missingVariable} is not available on this line.`)
        } else if (selection === null) {
            console.debug(`Skipped "${row.modifier_name}" default #${row.index}: "${rendered.value}" does not fit a ${group.modifier_type} option.`)
        } else {
            return selection
        }
    }

    return null
}
